import get from 'lodash/get';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import some from 'lodash/some';

import moment from 'moment-timezone';
import stringHash from 'string-hash';
import normalizeUrl from 'normalize-url';
import URL from 'url-parse';

import isMobileWidth from 'lib/isMobileWidth';

import {
  ALIGNMENTS,
  CONFIG_CHAT_THROTTLED,
  CONFIG_FETCH,
  CONFIG_UPDATE,
  CONFIG_FETCH_ERROR,
  CONFIG_OFFICE_CLOSED,
  DISPLAY_TYPE,
  RULE_TYPES,
  CONFIG_REFRESH_SUCCESS,
  SET_WELCOME_MESSAGE,
  WIDGET_TYPES,
} from 'app/modules/configuration/constants';
import { STAGES } from 'app/modules/ui';
import initialState from 'app/modules/configuration/initialState';
import {
  isClientReady,
  isAppInitialized,
  getIsMinimized,
  getProactiveTriggered,
  getStage,
  hasActiveSession,
} from 'app/modules';
import { hideWidget, showWidget, unminimizeWidget } from 'app/modules/ui/actions';
import {
  showOnboarding,
  beginChat,
  showDemoSelfService,
  showSelfService,
  suppress,
  unsuppress,
} from 'app/modules/ui/actions';
import { setSessionWelcomeMessage } from 'app/modules/session';
import { proactiveTriggered } from 'app/modules/app';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

export default function configurationReducer(state = {}, action) {
  let { type, payload } = action;
  let config;
  switch (type) {
    case CONFIG_FETCH:
      return { ...state, _isFetching: true };

    case CONFIG_UPDATE:
      let cohortId = payload.cohortId;
      config = merge({}, state.config, omit(payload.config, ['cohortId']));
      return {
        ...state,
        config,
        cohortId: cohortId ? cohortId : state.cohortId,
        _isFetching: false,
      };
    case CONFIG_FETCH_ERROR:
      return {
        ...state,
        _isFetching: false,
        error: payload.error,
      };
    case CONFIG_REFRESH_SUCCESS:
      // removing 'welcomeMessage' since other actions can update this current value
      config = merge({}, state.config, omit(payload.config, ['welcomeMessage']));
      return {
        ...state,
        config,
      };
    case SET_WELCOME_MESSAGE:
      config = state.config;
      config.welcomeMessage = payload;
      return {
        ...state,
        config,
      };
    default:
      return state;
  }
}

/*
 * actions
 */
export function fetchConfig() {
  return { type: CONFIG_FETCH };
}

function updateConfig({ config, cohortId }) {
  return { type: CONFIG_UPDATE, payload: { config, cohortId } };
}

export function fetchConfigError(error) {
  return { type: CONFIG_FETCH_ERROR, payload: { error } };
}

export const throttleChat = () => ({
  type: CONFIG_CHAT_THROTTLED,
});

export const closeOffice = () => ({
  type: CONFIG_OFFICE_CLOSED,
});

export const setWelcomeMessage = message => ({ type: SET_WELCOME_MESSAGE, payload: message });

/*
 * Thunk Actions
 */

export const fetchConfigSuccess = ({ config, cohortId }) => {
  return (dispatch, getState) => {
    dispatch(updateConfig({ config, cohortId }));
    dispatch(evaluateStage());
  };
};

export const evaluateStage = () => {
  return (dispatch, getState) => {
    const state = getState();
    // if kill switch is enable, don't change any stages. Widget will be hidden by default.
    if (getKillswitchIsOn(state.configuration)) {
      return;
    }

    // if app is not fully initialized, do not evaluate rules
    if (!isAppInitialized(state)) {
      return;
    }

    const newStage = evaluateStageChange(state, dispatch);

    if (newStage === STAGES.CHAT_CLOSED || newStage === STAGES.CHAT_THROTTLED) {
      if (getIsMinimized(state)) {
        dispatch(hideWidget());
      }
    }
  };
};

export const evaluateStageChange = (state, dispatch) => {
  if (hasActiveSession(state)) {
    dispatch(beginChat());
    return STAGES.CHAT;
  }

  //Show self service if it's enabled AND the user is NOT already on either CHAT or ONBOARDING
  let currentStage = getStage(state);
  if (
    isDemoSelfServiceEnabled(state.configuration) &&
    !(currentStage === STAGES.CHAT || currentStage === STAGES.ONBOARDING)
  ) {
    dispatch(showDemoSelfService());
    return STAGES.DEMO_SELF_SERVICE;
  }

  if (
    getWidgetType(state.configuration) === WIDGET_TYPES.SELF_SERVICE &&
    !(currentStage === STAGES.CHAT || currentStage === STAGES.ONBOARDING)
  ) {
    dispatch(showSelfService());
    return STAGES.SELF_SERVICE;
  }

  if (!getWithinOfficeHours(state.configuration)) {
    dispatch(closeOffice());
    return STAGES.CHAT_CLOSED;
  }

  if (!getInPercentageRollout(state.configuration)) {
    dispatch(throttleChat());
    return STAGES.CHAT_THROTTLED;
  }

  if (isClientReady(state)) {
    dispatch(beginChat());
    return STAGES.CHAT;
  }

  if (getOnboardingEnabled(state.configuration)) {
    dispatch(showOnboarding());
    return STAGES.ONBOARDING;
  }

  dispatch(beginChat());
  return STAGES.CHAT;
};

export const evaluateRules = (getState, dispatch) => {
  let state = getState();
  const rule = getRule(state.configuration);
  const display = getDisplay(state.configuration);
  if (display == DISPLAY_TYPE.ALL_PAGES || hasActiveSession(state) || getKillswitchIsOn(state.configuration)) {
    // no rules applies
    return;
  }

  dispatch(suppress());
  if (!rule) {
    return;
  }

  dispatch(setSessionWelcomeMessage(rule.message));

  let proactiveHasTriggered = getProactiveTriggered(state);
  let delay = proactiveHasTriggered && rule.proactive ? 0 : rule.delay;
  if (delay) {
    setTimeout(() => {
      executeRule(getState, dispatch);
    }, delay);
  } else {
    executeRule(getState, dispatch);
  }
};

const executeRule = (getState, dispatch) => {
  const state = getState();
  const rule = getRule(state.configuration);
  const proactiveHasTriggered = getProactiveTriggered(state);
  dispatch(unsuppress());
  if (rule.proactive && !isMobileWidth() && !proactiveHasTriggered) {
    dispatch(unminimizeWidget());
    dispatch(showWidget());
    dispatch(proactiveTriggered());
  }
};

/*
 * Should be called once, just before appInitialized is called
 */
export const checkRules = () => {
  return (dispatch, getState) => {
    evaluateRules(getState, dispatch);
  };
};

/*
 * selectors
 */
export const getAlignment = ({ config: { alignment } }) => {
  if (alignment === ALIGNMENTS.LEFT) {
    return alignment;
  }
  return ALIGNMENTS.RIGHT;
};

export const getBorder = ({ config }) => {
  return get(config, 'border');
};

export const getBorderRadius = ({ config }) => {
  return get(config, 'borderRadius');
};

export const getColors = ({ config }) => {
  let colors = {};
  if ('colors' in config && config.colors != {}) {
    colors = config.colors;
    if ((colors.brandPrimary == null) | (colors.brandPrimary == '')) {
      colors.brandPrimary = '#f3f3f3';
    }

    if ((colors.brandSecondary == null) | (colors.brandSecondary == '')) {
      colors.brandSecondary = '#ffffff';
    }
  } else {
    colors.brandPrimary = '#f3f3f3';
    colors.brandSecondary = '#ffffff';
  }
  return colors;
};

export const getDropshadowEnabled = ({ config }) => {
  if ('dropshadowEnabled' in config) {
    return config.dropshadowEnabled;
  } else {
    return false;
  }
};

export const getFont = ({ config }) => {
  return config.font;
};

export const getInitialChatButton = ({ config }) => config.initialChatButton;

export const getIsDraggable = ({ config }) => config.isDraggable;

export const isDemoSelfServiceEnabled = ({ config }) => config.demoSelfServiceEnabled;

export const getWidgetType = ({ config }) => {
  return config.widgetType === WIDGET_TYPES.SELF_SERVICE ? WIDGET_TYPES.SELF_SERVICE : WIDGET_TYPES.CHAT;
};

export const getOnboarding = ({ config }) => config.onboarding;

export const getOngoingChat = ({ config }) => {
  return config.ongoingChat;
};

export const getSelfService = ({ config }) => {
  return config.selfService;
};

export const getRules = ({ config }) => config.rules || {};

export const getDisplay = ({ config }) => config.display;

export const getRule = config => {
  let ruleUrl;
  const url = URL(normalizeUrl(window.location.toString()));
  if (!url) {
    return null;
  } else {
    ruleUrl = url.toString().replace(`${url.protocol}//`, '');
  }
  const rules = getRules(config);
  let rule;
  let matchedRule;
  let match = false;
  for (let key in rules) {
    rule = rules[key];
    switch (rule.type) {
      case RULE_TYPES.CONTAINS:
        match = ruleUrl.indexOf(key) > -1;
        break;
      case RULE_TYPES.EXACT:
        match = ruleUrl === key ? true : false;
        break;
      case RULE_TYPES.REGEX:
        let regex = new RegExp(key, 'gi');
        match = ruleUrl.match(regex);
        break;
      default:
        break;
    }
    if (match) {
      matchedRule = rule;
      break;
    }
  }
  return matchedRule;
};

export const getOnboardingEnabled = ({ config }) => {
  let onboarding = getOnboarding({ config });
  return !!onboarding && onboarding.enabled;
};

export const getWelcomeMessage = ({ config }) => config.welcomeMessage;

export const getKillswitchIsOn = ({ config }) => !config.enabled;

const getOfficeHours = config => {
  let officeHours = {};
  if ('officeHours' in config) {
    officeHours = config.officeHours;
  }
  return officeHours;
};

export const getWithinOfficeHours = ({ config }) => {
  const officeHours = getOfficeHours(config);
  return filterOfficeHours(officeHours, (now, start, stop) => now.isBetween(start, stop));
};

export const getZIndex = ({ config }) => {
  return config.zIndex ? config.zIndex : 2147483000;
};

//filterFn takes now, start and stop times
const filterOfficeHours = (officeHours, filterFn) => {
  let timezone = officeHours.timezone;
  let day = moment
    .tz(timezone)
    .format('dddd')
    .toLowerCase();
  let officeHoursList = officeHours[day];
  let now = moment.tz(timezone);
  let todayDate = now.format('YYYY-MM-DD');

  return some(officeHoursList, officeHours => {
    let start = moment.tz(`${todayDate} ${officeHours.start}`, DATE_FORMAT, timezone);
    let stop = moment.tz(`${todayDate} ${officeHours.stop}`, DATE_FORMAT, timezone);

    if (!start.isValid() || !stop.isValid()) {
      throw new Error('invalid moment! check config file for timestamp syntax errors');
    }

    return filterFn(now, start, stop);
  });
};

export const getInPercentageRollout = ({ config, cohortId }) => {
  return stringHash(cohortId) % 100 <= config.percentageRollout;
};

export const isSmoochEnabled = ({ config }) => {
  return config.provider === 'SMOOCH';
};

export { initialState };
